// frontend/src/pages/crm/VisualWorkflowBuilder.js
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useBranding } from '../../contexts/BrandingContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { workflowAPI, formsAPI, emailTemplateAPI, usersAPI, leadAPI, opportunityAPI, campaignsAPI, adminSystemAPI } from '../../utils/crmApi';
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    addEdge,
    Connection,
    Edge,
    Node,
    NodeTypes,
    EdgeTypes,
    MarkerType,
    Position,
    ReactFlowProvider,
    useReactFlow
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import {
    ArrowLeftIcon,
    PlayIcon,
    PauseIcon,
    TrashIcon,
    PlusIcon,
    CogIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    DocumentTextIcon,
    UserGroupIcon,
    EnvelopeIcon,
    ClipboardDocumentListIcon,
    Squares2X2Icon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import WorkflowNode from '../../components/crm/WorkflowNode';

// Custom Node Components
const TriggerNode = ({ data, selected }) => (
    <div className={`px-4 py-2 shadow-md rounded-md bg-card border-2 ${selected ? 'border-blue-500' : 'border-border'}`}>
        <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div className="font-bold text-sm">{data.label}</div>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{data.description}</div>
    </div>
);

const ActionNode = ({ data, selected }) => (
    <div className={`px-4 py-2 shadow-md rounded-md bg-card border-2 ${selected ? 'border-blue-500' : 'border-border'}`}>
        <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${data.type === 'email' ? 'bg-blue-500' : data.type === 'task' ? 'bg-yellow-500' : 'bg-purple-500'}`}></div>
            <div className="font-bold text-sm">{data.label}</div>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{data.description}</div>
    </div>
);

const ConditionNode = ({ data, selected }) => (
    <div className={`px-4 py-2 shadow-md rounded-md bg-card border-2 ${selected ? 'border-blue-500' : 'border-border'}`}>
        <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <div className="font-bold text-sm">{data.label}</div>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{data.description}</div>
    </div>
);

const nodeTypes = {
    trigger: WorkflowNode,
    action: WorkflowNode,
    condition: WorkflowNode,
};

// Dagre layout calculation
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: direction, nodesep: 100, ranksep: 150 });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: 200, height: 100 });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    nodes.forEach((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        node.targetPosition = Position.Top;
        node.sourcePosition = Position.Bottom;
        // We are shifting the dagre node position (anchor=center center) to the top left
        // so it matches the React Flow node anchor point (top left).
        node.position = {
            x: nodeWithPosition.x - 100, // width/2
            y: nodeWithPosition.y - 50, // height/2
        };
    });

    return { nodes, edges };
};

// Inner component that has access to ReactFlow instance
const WorkflowCanvas = ({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onDrop, onDragOver, nodeTypes, branding, setEdges, onLayout, onNodeClick, lastAddedNodeId, onFitView }) => {
    const { fitView, getNode, screenToFlowPosition, getViewport } = useReactFlow();
    const prevNodesCountRef = useRef(nodes.length);
    const lastNodeIdRef = useRef(null);
    
    // Expose fitView to parent component
    useEffect(() => {
        if (onFitView) {
            onFitView(() => {
                fitView({
                    padding: 0.2,
                    duration: 400,
                    maxZoom: 1.2,
                    minZoom: 0.3
                });
            });
        }
    }, [fitView, onFitView]);

    // Auto-fit view and pan to newly added node
    useEffect(() => {
        const currentNodesCount = nodes.length;
        const prevNodesCount = prevNodesCountRef.current;
        
        // Check if a new node was added
        if (currentNodesCount > prevNodesCount && nodes.length > 0) {
            // If we have a last added node ID, pan to it
            if (lastAddedNodeId) {
            setTimeout(() => {
                    const node = getNode(lastAddedNodeId);
                    if (node) {
                        // Pan to the new node
                        const viewport = getViewport();
                        const nodePosition = {
                            x: node.position.x + (node.width || 200) / 2,
                            y: node.position.y + (node.height || 100) / 2
                        };
                        
                        // Center the view on the new node
                        fitView({
                            padding: 0.3,
                            duration: 400,
                            maxZoom: 1.2,
                            minZoom: 0.3,
                            includeHiddenNodes: false
                        });
                    } else {
                        // Fallback: fit all nodes
                        fitView({
                            padding: 0.2,
                            duration: 400,
                            maxZoom: 1.2,
                            minZoom: 0.3
                        });
                    }
                }, 200);
            } else {
                // No specific node, just fit all
                setTimeout(() => {
                    fitView({
                        padding: 0.2,
                        duration: 400,
                        maxZoom: 1.2,
                        minZoom: 0.3
                    });
                }, 200);
            }
        } else if (currentNodesCount < prevNodesCount) {
            // Node removed, fit remaining nodes
            setTimeout(() => {
                if (nodes.length > 0) {
                fitView({
                    padding: 0.2,
                    duration: 300,
                        maxZoom: 1.2,
                        minZoom: 0.3
                });
                }
            }, 100);
        }
        
        prevNodesCountRef.current = currentNodesCount;
    }, [nodes.length, lastAddedNodeId, fitView, getNode, getViewport]);

    // Initial fit view on mount if nodes exist
    useEffect(() => {
        if (nodes.length > 0) {
            setTimeout(() => {
                fitView({ 
                    padding: 0.2, 
                    duration: 400,
                    maxZoom: 1.2,
                    minZoom: 0.3
                });
            }, 300);
        }
    }, []); // Only run on mount

    const onNodesDelete = useCallback((deleted) => {
        // ReactFlow's onNodesDelete handles the deletion automatically
        // We just need to ensure edges are also removed when nodes are deleted
        const deletedNodeIds = deleted.map(node => node.id);
        setEdges((eds) => eds.filter(edge => 
            !deletedNodeIds.includes(edge.source) && !deletedNodeIds.includes(edge.target)
        ));
    }, [setEdges]);
    
    const onEdgesDelete = useCallback((deleted) => {
        // ReactFlow automatically removes deleted edges, but we can add logging or validation here if needed
        console.log('Edges deleted:', deleted);
    }, []);

    // Wrap onDrop to convert screen coordinates to flow coordinates
    const handleDrop = useCallback((event) => {
        event.preventDefault();
        const paletteNodeData = event.dataTransfer.getData('application/reactflow');
        
        if (!paletteNodeData) return;
        
        // Convert screen coordinates to flow coordinates
        const position = screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
        });
        
        // Call the parent onDrop with converted position
        onDrop(event, position);
    }, [screenToFlowPosition, onDrop]);

    return (
        <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodesDelete={onNodesDelete}
            onEdgesDelete={onEdgesDelete}
            onNodeClick={onNodeClick}
            onConnect={onConnect}
            deleteKeyCode="Delete"
            multiSelectionKeyCode={['Meta', 'Control']}
            onDrop={handleDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            fitView
            attributionPosition="bottom-left"
            connectionLineType="smoothstep"
            defaultEdgeOptions={{
                type: 'smoothstep',
                markerEnd: { type: MarkerType.ArrowClosed },
                deletable: true,
                selectable: true
            }}
            edgesUpdatable={false}
            edgesFocusable={true}
        >
            <Controls showFitView={false} showInteractive={true} />
            <MiniMap 
                nodeColor={(node) => {
                    switch (node.type) {
                        case 'trigger': return branding?.secondaryColor || '#10b981';
                        case 'action': return branding?.primaryColor || '#3b82f6';
                        case 'condition': return branding?.accentColor || '#f59e0b';
                        default: return '#6b7280';
                    }
                }}
                position="bottom-right"
            />
            <Background variant="dots" gap={12} size={1} />
        </ReactFlow>
    );
};

const VisualWorkflowBuilder = () => {
    const { branding } = useBranding();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const workflowId = searchParams.get('id');
    const isGlobalTemplate = searchParams.get('global') === 'true';
    const [loading, setLoading] = useState(false);
    const [loadingWorkflow, setLoadingWorkflow] = useState(!!workflowId);
    const [workflowName, setWorkflowName] = useState('');
    const [workflowDescription, setWorkflowDescription] = useState('');
    const [workflowCategory, setWorkflowCategory] = useState('task_automation');
    
    // Load data for parameter selection
    useEffect(() => {
        const loadData = async () => {
            // Load forms
            try {
                setFormsLoading(true);
                const { data } = await formsAPI.getAll({ status: 'published' });
                setForms(data?.data || []);
            } catch (error) {
                console.error('Failed to load forms:', error);
            } finally {
                setFormsLoading(false);
            }
            
            // Load email templates
            try {
                setEmailTemplatesLoading(true);
                const { data } = await emailTemplateAPI.getAll();
                setEmailTemplates(data?.data || []);
            } catch (error) {
                console.error('Failed to load email templates:', error);
            } finally {
                setEmailTemplatesLoading(false);
            }
            
            // Load users
            try {
                setUsersLoading(true);
                const { data } = await usersAPI.getAll();
                setUsers(data?.data || []);
            } catch (error) {
                console.error('Failed to load users:', error);
            } finally {
                setUsersLoading(false);
            }
            
            // Load leads
            try {
                setLeadsLoading(true);
                const { data } = await leadAPI.getAll({ limit: 100 });
                setLeads(data?.data || []);
            } catch (error) {
                console.error('Failed to load leads:', error);
            } finally {
                setLeadsLoading(false);
            }
            
            // Load opportunities
            try {
                setOpportunitiesLoading(true);
                const { data } = await opportunityAPI.getAll({ limit: 100 });
                setOpportunities(data?.data || []);
            } catch (error) {
                console.error('Failed to load opportunities:', error);
            } finally {
                setOpportunitiesLoading(false);
            }
            
            // Load campaigns (for start_campaign action)
            try {
                setCampaignsLoading(true);
                const { data } = await campaignsAPI.getAll({ type: 'journey', limit: 100 });
                setCampaigns(data?.data || []);
            } catch (error) {
                console.error('Failed to load campaigns:', error);
            } finally {
                setCampaignsLoading(false);
            }
        };
        
        loadData();
    }, []);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const lastNodeIdRef = useRef(null);
    const [lastAddedNodeId, setLastAddedNodeId] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const workflowLoadedRef = useRef(false);
    const reactFlowInstanceRef = useRef(null);
    const fitViewRef = useRef(null);
    
    // Data for parameter selection
    const [forms, setForms] = useState([]);
    const [formsLoading, setFormsLoading] = useState(false);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [leads, setLeads] = useState([]);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [opportunities, setOpportunities] = useState([]);
    const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);

    // Available node types for the palette
    const nodePalette = useMemo(() => [
        // Client triggers
        {
            type: 'trigger',
            label: 'Client Created',
            description: 'When a new client is added',
            data: { label: 'Client Created', description: 'When a new client is added', type: 'client_created', category: 'client' }
        },
        {
            type: 'trigger',
            label: 'Client Updated',
            description: 'When a client is updated',
            data: { label: 'Client Updated', description: 'When a client is updated', type: 'client_updated', category: 'client' }
        },
        {
            type: 'trigger',
            label: 'Client Status Changed',
            description: 'When client status changes',
            data: { label: 'Client Status Changed', description: 'When client status changes', type: 'client_status_changed', category: 'client' }
        },
        // Task triggers
        {
            type: 'trigger',
            label: 'Task Created',
            description: 'When a task is created',
            data: { label: 'Task Created', description: 'When a task is created', type: 'task_created', category: 'task' }
        },
        {
            type: 'trigger',
            label: 'Task Completed',
            description: 'When a task is marked complete',
            data: { label: 'Task Completed', description: 'When a task is marked complete', type: 'task_completed', category: 'task' }
        },
        {
            type: 'trigger',
            label: 'Task Updated',
            description: 'When a task is updated',
            data: { label: 'Task Updated', description: 'When a task is updated', type: 'task_updated', category: 'task' }
        },
        // Lead triggers
        {
            type: 'trigger',
            label: 'Lead Created',
            description: 'When a new lead is created',
            data: { label: 'Lead Created', description: 'When a new lead is created', type: 'lead_created', category: 'lead' }
        },
        {
            type: 'trigger',
            label: 'Lead Updated',
            description: 'When a lead is updated',
            data: { label: 'Lead Updated', description: 'When a lead is updated', type: 'lead_updated', category: 'lead' }
        },
        {
            type: 'trigger',
            label: 'Lead Qualified',
            description: 'When a lead is qualified',
            data: { label: 'Lead Qualified', description: 'When a lead is qualified', type: 'lead_qualified', category: 'lead' }
        },
        {
            type: 'trigger',
            label: 'Lead Converted',
            description: 'When a lead is converted to client',
            data: { label: 'Lead Converted', description: 'When a lead is converted to client', type: 'lead_converted', category: 'lead' }
        },
        // Email triggers
        {
            type: 'trigger',
            label: 'Email Opened',
            description: 'When an email is opened',
            data: { label: 'Email Opened', description: 'When an email is opened', type: 'email_opened', category: 'email', needsFilter: true }
        },
        {
            type: 'trigger',
            label: 'Email Clicked',
            description: 'When a link in email is clicked',
            data: { label: 'Email Clicked', description: 'When a link in email is clicked', type: 'email_clicked', category: 'email', needsFilter: true }
        },
        {
            type: 'trigger',
            label: 'Email Replied',
            description: 'When an email is replied to',
            data: { label: 'Email Replied', description: 'When an email is replied to', type: 'email_replied', category: 'email', needsFilter: true }
        },
        {
            type: 'trigger',
            label: 'Email Sent',
            description: 'When an email is sent',
            data: { label: 'Email Sent', description: 'When an email is sent', type: 'email_sent', category: 'email', needsFilter: true }
        },
        // Form triggers
        {
            type: 'trigger',
            label: 'Form Submitted',
            description: 'When a form is submitted',
            data: { label: 'Form Submitted', description: 'When a form is submitted', type: 'form_submitted', category: 'form', needsFilter: true }
        },
        {
            type: 'trigger',
            label: 'Form Started',
            description: 'When a form is started',
            data: { label: 'Form Started', description: 'When a form is started', type: 'form_started', category: 'form', needsFilter: true }
        },
        // Website triggers
        {
            type: 'trigger',
            label: 'URL Visited',
            description: 'When a URL is visited',
            data: { label: 'URL Visited', description: 'When a URL is visited', type: 'url_visited', category: 'website', needsFilter: true }
        },
        {
            type: 'trigger',
            label: 'Link Clicked',
            description: 'When a link is clicked',
            data: { label: 'Link Clicked', description: 'When a link is clicked', type: 'link_clicked', category: 'website', needsFilter: true }
        },
        {
            type: 'trigger',
            label: 'Resource Downloaded',
            description: 'When a resource is downloaded',
            data: { label: 'Resource Downloaded', description: 'When a resource is downloaded', type: 'resource_downloaded', category: 'website', needsFilter: true }
        },
        // Opportunity triggers
        {
            type: 'trigger',
            label: 'Opportunity Created',
            description: 'When an opportunity is created',
            data: { label: 'Opportunity Created', description: 'When an opportunity is created', type: 'opportunity_created', category: 'opportunity' }
        },
        {
            type: 'action',
            label: 'Send Email',
            description: 'Send an email notification',
            data: { label: 'Send Email', description: 'Send an email notification', type: 'send_email' }
        },
        {
            type: 'action',
            label: 'Create Task',
            description: 'Create a new task',
            data: { label: 'Create Task', description: 'Create a new task', type: 'create_task' }
        },
        {
            type: 'action',
            label: 'Assign Task',
            description: 'Assign task to user',
            data: { label: 'Assign Task', description: 'Assign task to user', type: 'assign_user' }
        },
        {
            type: 'action',
            label: 'Send Survey',
            description: 'Send a survey/form to client',
            data: { label: 'Send Survey', description: 'Send a survey/form to client', type: 'send_survey' }
        },
        {
            type: 'action',
            label: 'Update Status',
            description: 'Update entity status',
            data: { label: 'Update Status', description: 'Update entity status', type: 'update_status' }
        },
        {
            type: 'action',
            label: 'Send Notification',
            description: 'Send a notification',
            data: { label: 'Send Notification', description: 'Send a notification', type: 'send_notification' }
        },
        {
            type: 'action',
            label: 'Delay',
            description: 'Add a delay before next action',
            data: { label: 'Delay', description: 'Add a delay before next action', type: 'delay' }
        },
        {
            type: 'action',
            label: 'Webhook Call',
            description: 'Call an external webhook',
            data: { label: 'Webhook Call', description: 'Call an external webhook', type: 'webhook_call' }
        },
        // Lead actions
        {
            type: 'action',
            label: 'Create Lead',
            description: 'Create a new lead',
            data: { label: 'Create Lead', description: 'Create a new lead', type: 'create_lead' }
        },
        {
            type: 'action',
            label: 'Update Lead',
            description: 'Update an existing lead',
            data: { label: 'Update Lead', description: 'Update an existing lead', type: 'update_lead' }
        },
        {
            type: 'action',
            label: 'Delete Lead',
            description: 'Delete a lead',
            data: { label: 'Delete Lead', description: 'Delete a lead', type: 'delete_lead' }
        },
        {
            type: 'action',
            label: 'Convert Lead',
            description: 'Convert lead to client',
            data: { label: 'Convert Lead', description: 'Convert lead to client', type: 'convert_lead' }
        },
        // Opportunity actions
        {
            type: 'action',
            label: 'Create Opportunity',
            description: 'Create a new opportunity',
            data: { label: 'Create Opportunity', description: 'Create a new opportunity', type: 'create_opportunity' }
        },
        {
            type: 'action',
            label: 'Update Opportunity',
            description: 'Update an existing opportunity',
            data: { label: 'Update Opportunity', description: 'Update an existing opportunity', type: 'update_opportunity' }
        },
        {
            type: 'action',
            label: 'Delete Opportunity',
            description: 'Delete an opportunity',
            data: { label: 'Delete Opportunity', description: 'Delete an opportunity', type: 'delete_opportunity' }
        },
        // Entity updates
        {
            type: 'action',
            label: 'Update Entity Params',
            description: 'Update entity parameters',
            data: { label: 'Update Entity Params', description: 'Update entity parameters', type: 'update_entity_params' }
        },
        {
            type: 'action',
            label: 'Start Campaign',
            description: 'Start an email campaign journey',
            data: { label: 'Start Campaign', description: 'Start an email campaign journey', type: 'start_campaign' }
        },
        {
            type: 'condition',
            label: 'If/Else',
            description: 'Conditional logic',
            data: { label: 'If/Else', description: 'Conditional logic', type: 'condition_check' }
        }
    ], []);

    // Update node data with template/form names when they're loaded
    useEffect(() => {
        if (emailTemplates.length === 0 && forms.length === 0) return;
        
        setNodes(currentNodes => currentNodes.map(node => {
            const updatedData = { ...node.data };
            
            // Update email template name if templateId exists
            if (node.data?.email?.templateId && !node.data.email.templateName) {
                const template = emailTemplates.find(t => t._id === node.data.email.templateId);
                if (template) {
                    updatedData.email = {
                        ...node.data.email,
                        templateName: template.name
                    };
                }
            }
            
            // Update form name if formId exists
            if (node.data?.survey?.formId && !node.data.survey.formName) {
                const form = forms.find(f => f._id === node.data.survey.formId);
                if (form) {
                    updatedData.survey = {
                        ...node.data.survey,
                        formName: form.name
                    };
                }
            }
            
            if (JSON.stringify(updatedData) !== JSON.stringify(node.data)) {
                return { ...node, data: updatedData };
            }
            return node;
        }));
    }, [emailTemplates, forms, setNodes]);

    // Load existing workflow if editing (must be after nodePalette is defined)
    useEffect(() => {
        const loadWorkflow = async () => {
            if (!workflowId || workflowLoadedRef.current) return;
            
            try {
                setLoadingWorkflow(true);
                console.log('Loading workflow with ID:', workflowId);
                const response = await workflowAPI.getById(workflowId);
                console.log('Workflow API response:', response);
                
                if (!response || !response.data) {
                    throw new Error('Invalid response from server');
                }
                
                if (response.data?.success && response.data.data) {
                    const workflow = response.data.data;
                    console.log('Workflow data loaded:', workflow);
                    
                    // Set form fields
                    setWorkflowName(workflow.name || '');
                    setWorkflowDescription(workflow.description || '');
                    setWorkflowCategory(workflow.category || 'task_automation');
                    
                    // Convert workflow to nodes
                    const newNodes = [];
                    const newEdges = [];
                    const conditionEdgesToCreate = []; // Declare outside if block so it's accessible later
                    
                    // Create trigger node
                    if (workflow.trigger) {
                        const triggerEvent = workflow.trigger.event || 'client_created';
                        const triggerNodeId = `trigger-${Date.now()}`;
                        
                        // Find the trigger label from nodePalette
                        const triggerPalette = nodePalette.find(n => n.type === 'trigger' && n.data.type === triggerEvent);
                        const triggerLabel = triggerPalette?.label || triggerEvent.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        const triggerCategory = triggerPalette?.data?.category || 'client'; // Default to 'client' if not found
                        
                        newNodes.push({
                            id: triggerNodeId,
                            type: 'trigger',
                            position: { x: 250, y: 50 },
                            data: {
                                label: triggerLabel,
                                description: triggerPalette?.description || '',
                                type: triggerEvent,
                                category: triggerCategory,
                                eventFilters: workflow.trigger.eventFilters || {}
                            }
                        });
                        
                        // Create action and condition nodes
                        if (workflow.actions && workflow.actions.length > 0) {
                            workflow.actions.forEach((action, index) => {
                                // Check if this is a condition_check action - convert to condition node
                                const isCondition = action.type === 'condition_check';
                                const nodeType = isCondition ? 'condition' : 'action';
                                const nodeId = isCondition ? `condition-${Date.now()}-${index}` : `action-${Date.now()}-${index}`;
                                
                                // Find the node label from nodePalette
                                const nodePaletteItem = isCondition 
                                    ? nodePalette.find(n => n.type === 'condition')
                                    : nodePalette.find(n => n.type === 'action' && n.data.type === action.type);
                                const nodeLabel = nodePaletteItem?.label || action.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                
                                // Build node data from action
                                const nodeData = {
                                    label: nodeLabel,
                                    description: nodePaletteItem?.description || '',
                                    type: action.type
                                };
                                
                                // Add condition data if it's a condition_check
                                if (isCondition && action.condition) {
                                    nodeData.condition = {
                                        field: action.condition.field || '',
                                        operator: action.condition.operator || '',
                                        value: action.condition.value || '',
                                        trueActions: action.condition.trueActions || [],
                                        falseActions: action.condition.falseActions || []
                                    };
                                }
                                
                                // Add action-specific parameters
                                if (action.email) {
                                    const templateId = action.email.template?._id || action.email.template || action.email.templateId;
                                    const template = emailTemplates.find(t => t._id === templateId);
                                    nodeData.email = {
                                        templateId: templateId,
                                        templateName: template?.name || action.email.template?.name || '',
                                        subject: action.email.subject || '',
                                        content: action.email.content || '',
                                        recipients: action.email.recipients || [],
                                        recipientType: action.email.recipientType || (action.email.recipients && action.email.recipients.length > 0 ? 'custom' : 'client'),
                                        variables: action.email.variables || {}
                                    };
                                }
                                if (action.survey) {
                                    const formId = action.survey.formId;
                                    const form = forms.find(f => f._id === formId);
                                    nodeData.survey = {
                                        formId: formId,
                                        formName: form?.name || '',
                                        recipientType: action.survey.recipientType || 'client',
                                        recipientEmail: action.survey.recipientEmail || '',
                                        message: action.survey.message || ''
                                    };
                                }
                                if (action.task) {
                                    nodeData.task = {
                                        title: action.task.title || '',
                                        category: action.task.category || 'other',
                                        priority: action.task.priority || 'medium',
                                        description: action.task.description || ''
                                    };
                                }
                                if (action.statusUpdate) {
                                    nodeData.statusUpdate = {
                                        entity: action.statusUpdate.entity || 'client',
                                        status: action.statusUpdate.status || ''
                                    };
                                }
                                if (action.assignment) {
                                    nodeData.assignment = {
                                        entity: action.assignment.entity || 'client',
                                        userId: action.assignment.userId
                                    };
                                }
                                if (action.delay) {
                                    nodeData.delay = {
                                        amount: action.delay.amount || 0,
                                        unit: action.delay.unit || 'minutes'
                                    };
                                }
                                if (action.webhook) {
                                    nodeData.webhook = {
                                        url: action.webhook.url || '',
                                        method: action.webhook.method || 'POST',
                                        headers: action.webhook.headers || {},
                                        body: action.webhook.body || ''
                                    };
                                }
                                if (action.notification) {
                                    nodeData.notification = {
                                        message: action.notification.message || '',
                                        type: action.notification.type || 'info'
                                    };
                                }
                                if (action.lead) {
                                    // For update_lead, parameters are in action.lead.updates
                                    // For create_lead, parameters are directly in action.lead
                                    if (action.type === 'update_lead') {
                                        nodeData.lead = {
                                            leadId: action.lead.leadId || '',
                                            updates: action.lead.updates || {},
                                            scoreIncrement: action.lead.scoreIncrement
                                        };
                                    } else {
                                        // For create_lead, convert_lead, delete_lead
                                        nodeData.lead = {
                                            leadId: action.lead.leadId,
                                            firstName: action.lead.firstName || '',
                                            lastName: action.lead.lastName || '',
                                            email: action.lead.email || '',
                                            phoneE164: action.lead.phoneE164 || '',
                                            company: action.lead.company || '',
                                            jobTitle: action.lead.jobTitle || '',
                                            source: action.lead.source || '',
                                            status: action.lead.status || 'new',
                                            assignedTo: action.lead.assignedTo || '',
                                            tags: action.lead.tags || [],
                                            customFields: action.lead.customFields || {}
                                        };
                                    }
                                }
                                if (action.opportunity) {
                                    // For update_opportunity, parameters are in action.opportunity.updates
                                    // For create_opportunity, parameters are directly in action.opportunity
                                    if (action.type === 'update_opportunity') {
                                        nodeData.opportunity = {
                                            opportunityId: action.opportunity.opportunityId || '',
                                            updates: action.opportunity.updates || {}
                                        };
                                    } else {
                                        // For create_opportunity, delete_opportunity
                                        nodeData.opportunity = {
                                            opportunityId: action.opportunity.opportunityId,
                                            name: action.opportunity.name || '',
                                            amount: action.opportunity.amount || 0,
                                            stage: action.opportunity.stage || 'prospecting',
                                            probability: action.opportunity.probability || 0,
                                            expectedCloseDate: action.opportunity.expectedCloseDate || '',
                                            assignedTo: action.opportunity.assignedTo || '',
                                            description: action.opportunity.description || ''
                                        };
                                    }
                                }
                                if (action.entityUpdate) {
                                    nodeData.entityUpdate = {
                                        entity: action.entityUpdate.entity || '',
                                        entityId: action.entityUpdate.entityId || '',
                                        updates: typeof action.entityUpdate.updates === 'string' 
                                            ? action.entityUpdate.updates 
                                            : JSON.stringify(action.entityUpdate.updates || {}, null, 2)
                                    };
                                }
                                
                                newNodes.push({
                                    id: nodeId,
                                    type: nodeType,
                                    position: { x: 250, y: 200 + (index * 150) },
                                    data: nodeData
                                });
                                
                                // Create edge from trigger to first action/condition
                                // Always connect trigger to the first action, even if it's a condition
                                if (index === 0) {
                                    newEdges.push({
                                        id: `edge-${triggerNodeId}-${nodeId}`,
                                        source: triggerNodeId,
                                        target: nodeId,
                                        type: 'smoothstep',
                                        markerEnd: { type: MarkerType.ArrowClosed }
                                    });
                                } else {
                                    // For subsequent actions, create sequential edges only if:
                                    // 1. The current action is NOT in any condition branch (those are handled separately)
                                    // 2. The previous action is NOT a condition (conditions handle their own branches via true/false)
                                    
                                    // Check if this action is in any condition's branch
                                    const currentActionOrder = index + 1; // action.order is 1-based
                                    const isInConditionBranch = workflow.actions.some((a) => {
                                        if (a.type === 'condition_check' && a.condition) {
                                            const trueActions = a.condition.trueActions || [];
                                            const falseActions = a.condition.falseActions || [];
                                            return trueActions.includes(currentActionOrder) || falseActions.includes(currentActionOrder);
                                        }
                                        return false;
                                    });
                                    
                                    // Only create sequential edge if this action is not in a condition branch
                                    if (!isInConditionBranch) {
                                        const prevAction = workflow.actions[index - 1];
                                        const prevIsCondition = prevAction?.type === 'condition_check';
                                        
                                        // If previous is a condition, don't create sequential edge (condition branches handle connections)
                                        // Otherwise, connect to the previous node
                                        if (!prevIsCondition) {
                                            // Find the previous node (should be the last one we added, which is at newNodes.length - 1)
                                            // newNodes already includes the current node we just pushed, so previous is at length - 2
                                            // But we also have the trigger at index 0, so for action nodes:
                                            // - Trigger is at newNodes[0]
                                            // - First action is at newNodes[1] (index 0 in actions array)
                                            // - Second action is at newNodes[2] (index 1 in actions array)
                                            // So previous action node is at newNodes.length - 1 (before current push) or newNodes.length - 2 (after current push)
                                            const prevNodeIndex = newNodes.length - 2; // -2 because we just pushed current node
                                            if (prevNodeIndex >= 1) { // >= 1 to skip trigger
                                                const prevNode = newNodes[prevNodeIndex];
                                                if (prevNode && prevNode.id !== nodeId) { // Make sure it's not the same node
                                                    newEdges.push({
                                                        id: `edge-${prevNode.id}-${nodeId}`,
                                                        source: prevNode.id,
                                                        target: nodeId,
                                                        type: 'smoothstep',
                                                        markerEnd: { type: MarkerType.ArrowClosed }
                                                    });
                                                }
                                            }
                                        }
                                    }
                                }
                                
                                // For condition nodes, create edges based on trueActions and falseActions
                                if (isCondition && action.condition) {
                                    const trueActions = action.condition.trueActions || [];
                                    const falseActions = action.condition.falseActions || [];
                                    
                                    // Create edges for true branch
                                    trueActions.forEach((actionOrder) => {
                                        const targetNodeIndex = actionOrder - 1; // actionOrder is 1-based
                                        if (targetNodeIndex >= 0 && targetNodeIndex < workflow.actions.length) {
                                            const targetAction = workflow.actions[targetNodeIndex];
                                            // Find the corresponding node (will be created later or already exists)
                                            // We'll create these edges after all nodes are created
                                            // Store for later processing
                                            conditionEdgesToCreate.push({
                                                sourceId: nodeId,
                                                targetOrder: actionOrder,
                                                branch: 'true'
                                            });
                                        }
                                    });
                                    
                                    // Create edges for false branch
                                    falseActions.forEach((actionOrder) => {
                                        const targetNodeIndex = actionOrder - 1;
                                        if (targetNodeIndex >= 0 && targetNodeIndex < workflow.actions.length) {
                                            conditionEdgesToCreate.push({
                                                sourceId: nodeId,
                                                targetOrder: actionOrder,
                                                branch: 'false'
                                            });
                                        }
                                    });
                                }
                            });
                        }
                    }
                    
                    // Create condition edges after all nodes are created (only if we have edges to create)
                    if (conditionEdgesToCreate.length > 0) {
                        conditionEdgesToCreate.forEach((edgeInfo) => {
                            // Find target node by action order
                            // targetOrder is 1-based (first action = 1, second = 2, etc.)
                            // newNodes[0] is the trigger, so action nodes start at newNodes[1]
                            // Therefore: targetOrder 1 -> newNodes[1], targetOrder 2 -> newNodes[2], etc.
                            const targetNodeIndex = edgeInfo.targetOrder; // No -1 because trigger is at index 0
                            
                            if (targetNodeIndex >= 1 && targetNodeIndex < newNodes.length) {
                                const targetNode = newNodes[targetNodeIndex];
                                
                                // Find the source condition node by its ID
                                const sourceNode = newNodes.find(n => n.id === edgeInfo.sourceId);
                                
                                if (sourceNode && targetNode) {
                                    newEdges.push({
                                        id: `edge-${edgeInfo.sourceId}-${targetNode.id}-${edgeInfo.branch}`,
                                        source: edgeInfo.sourceId,
                                        target: targetNode.id,
                                        sourceHandle: edgeInfo.branch,
                                        type: 'smoothstep',
                                        markerEnd: { type: MarkerType.ArrowClosed },
                                        style: {
                                            stroke: edgeInfo.branch === 'true' ? '#10b981' : '#ef4444',
                                            strokeWidth: 2
                                        },
                                        data: { branch: edgeInfo.branch }
                                    });
                                }
                            }
                        });
                    }
                    
                    // Apply layout
                    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(newNodes, newEdges);
                    setNodes(layoutedNodes);
                    setEdges(layoutedEdges);
                    
                    workflowLoadedRef.current = true;
                }
            } catch (error) {
                console.error('Failed to load workflow:', error);
                console.error('Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                    stack: error.stack
                });
                
                let errorMessage = 'Failed to load workflow data';
                if (error.response?.status === 404) {
                    errorMessage = 'Workflow not found';
                } else if (error.response?.status === 401) {
                    errorMessage = 'Unauthorized. Please log in again.';
                } else if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                toast.error(errorMessage);
                // Reset the ref so user can retry
                workflowLoadedRef.current = false;
            } finally {
                setLoadingWorkflow(false);
            }
        };
        
        // nodePalette is a useMemo that returns an array, not a function
        // Wait for nodePalette to be available (has at least one item)
        if (nodePalette && nodePalette.length > 0) {
            loadWorkflow();
        } else {
            console.warn('nodePalette is not ready yet, waiting...');
        }
    }, [workflowId, nodePalette]);

    const onConnect = useCallback(
        (params) => {
            // For condition nodes, track which branch (true/false) the connection is from
            const edgeData = {
                type: 'smoothstep',
                markerEnd: { type: MarkerType.ArrowClosed }
            };
            
            // If sourceHandle is 'true' or 'false', add it to edge data and style
            if (params.sourceHandle === 'true' || params.sourceHandle === 'false') {
                edgeData.data = { branch: params.sourceHandle };
                // Color code the edge based on branch (green for true, red for false)
                edgeData.style = {
                    stroke: params.sourceHandle === 'true' ? '#10b981' : '#ef4444',
                    strokeWidth: 2.5
                };
            } else {
                // Default edge style for regular connections
                edgeData.style = {
                    stroke: '#94a3b8',
                    strokeWidth: 2
                };
            }
            
            setEdges((eds) => addEdge({ ...params, ...edgeData }, eds));
        },
        [setEdges]
    );

    const onDragStart = (event, paletteNode) => {
        // Store the full palette node data as JSON
        event.dataTransfer.setData('application/reactflow', JSON.stringify(paletteNode));
        event.dataTransfer.effectAllowed = 'move';
    };

    const onDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const onDrop = useCallback((event, flowPosition) => {
        const paletteNodeData = event.dataTransfer.getData('application/reactflow');
        
        if (!paletteNodeData) return;
        
        // Parse the palette node data
        const paletteNode = JSON.parse(paletteNodeData);
        
        // Use the flow position if provided, otherwise calculate smart placement
        let position = flowPosition;
        
        if (!position || nodes.length === 0) {
            // First node or no valid position: place in center of viewport
            position = { x: 400, y: 300 };
        } else {
            // Ensure position is reasonable (not too far off-screen)
            // If position seems off, place near existing nodes
            const existingNodes = nodes;
            if (existingNodes.length > 0) {
                const avgX = existingNodes.reduce((sum, n) => sum + n.position.x, 0) / existingNodes.length;
                const avgY = existingNodes.reduce((sum, n) => sum + n.position.y, 0) / existingNodes.length;
                
                // If dropped position is too far from existing nodes, place near them
                const distance = Math.sqrt(Math.pow(position.x - avgX, 2) + Math.pow(position.y - avgY, 2));
                if (distance > 1000) {
                    const maxX = Math.max(...existingNodes.map(n => n.position.x));
                    position = {
                        x: maxX + 250,
                        y: avgY
                    };
                }
            }
        }

        const newNodeId = `${paletteNode.type}-${Date.now()}`;
        lastNodeIdRef.current = newNodeId;
        setLastAddedNodeId(newNodeId); // Store for auto-panning
        
        const newNode = {
            id: newNodeId,
            type: paletteNode.type,
            position,
            data: {
                ...(paletteNode.data || { label: 'New Node' }),
                onDelete: handleDeleteNode // Pass delete handler to node
            },
        };

        setNodes((nds) => {
            const updated = nds.concat(newNode);
            // Auto-layout after adding node if there are multiple nodes
            if (updated.length > 1) {
                setTimeout(() => {
                    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(updated, edges);
                    setNodes(layoutedNodes);
                    setEdges(layoutedEdges);
                    // Clear the last added node ID after layout (layout will reposition it)
                    setTimeout(() => setLastAddedNodeId(null), 500);
                }, 50);
            }
            return updated;
        });
    }, [nodes, edges, setNodes, setEdges]);

    const handleSave = async () => {
        if (!workflowName.trim()) {
            toast.error('Please enter a workflow name');
            return;
        }

        if (nodes.length === 0) {
            toast.error('Please add at least one node to the workflow');
            return;
        }

            // Convert visual workflow to backend format
            const triggerNode = nodes.find(node => node.type === 'trigger');
            const actionNodes = nodes.filter(node => node.type === 'action');
            const conditionNodes = nodes.filter(node => node.type === 'condition');
        
        // Debug: Log all action nodes to see what's being processed
        console.log('All action nodes:', actionNodes.map(n => ({ 
            id: n.id, 
            type: n.data?.type, 
            label: n.data?.label 
        })));

        if (!triggerNode) {
            toast.error('Please add a trigger node to the workflow');
            return;
        }

        if (actionNodes.length === 0 && conditionNodes.length === 0) {
            toast.error('Please add at least one action or condition node to the workflow');
            return;
        }

        // Validate action and condition nodes are properly configured
        const incompleteActions = [...actionNodes, ...conditionNodes].filter(node => {
            if (node.data.type === 'update_entity_params') {
                const entity = node.data.entityUpdate?.entity;
                const entityId = node.data.entityUpdate?.entityId;
                let updates = node.data.entityUpdate?.updates;
                
                // Handle updates if it's a string (JSON) - parse it for validation
                if (typeof updates === 'string' && updates.trim()) {
                    try {
                        updates = JSON.parse(updates);
                    } catch (e) {
                        // Invalid JSON - mark as incomplete
                        console.warn('Invalid JSON in updates field during validation:', e);
                        updates = null;
                    }
                }
                
                const isValid = entity && entity.trim() && 
                    entityId && entityId.trim() && 
                    updates && 
                    typeof updates === 'object' && 
                    Object.keys(updates).length > 0;
                
                if (!isValid) {
                    console.warn('Incomplete update_entity_params action:', { 
                        entity: entity || 'missing', 
                        entityId: entityId || 'missing', 
                        updates: updates || 'missing',
                        updatesType: typeof updates,
                        updatesKeys: updates && typeof updates === 'object' ? Object.keys(updates).length : 0
                    });
                }
                return !isValid;
            } else if (node.data.type === 'send_survey') {
                return !node.data.survey?.formId;
            } else if (node.data.type === 'update_lead') {
                // Update lead can work without leadId (will auto-detect by email)
                return false;
            } else if (node.data.type === 'delete_lead' || node.data.type === 'convert_lead') {
                return !node.data.lead?.leadId;
            } else if (node.data.type === 'condition_check' || node.type === 'condition') {
                return !node.data.condition?.field || !node.data.condition?.operator;
            } else if (node.data.type === 'update_opportunity' || node.data.type === 'delete_opportunity') {
                return !node.data.opportunity?.opportunityId;
            }
            return false;
        });

        if (incompleteActions.length > 0) {
            const actionTypes = incompleteActions.map(n => n.data.type).join(', ');
            toast.error(`Please configure ${incompleteActions.length} action node(s) before saving: ${actionTypes}. Click on each action node to configure it.`, { duration: 6000 });
            return;
        }

        setLoading(true);
        try {

            // Create a combined array with proper order for action numbering
            const allNodes = [...actionNodes, ...conditionNodes].sort((a, b) => {
                // Sort by position Y coordinate to maintain visual order
                return (a.position?.y || 0) - (b.position?.y || 0);
            });

            const workflowData = {
                name: workflowName,
                description: workflowDescription,
                category: workflowCategory,
                trigger: {
                    type: 'event',
                    event: triggerNode?.data?.type || 'client_created',
                    conditions: [],
                    eventFilters: triggerNode?.data?.eventFilters || {}
                },
                actions: allNodes.map((node, index) => {
                    // Skip if node doesn't have a type
                    if (!node.data?.type) {
                        console.warn('Skipping action node without type:', node);
                        return null;
                    }
                    
                    const baseAction = {
                        type: node.data.type,
                        order: index + 1
                    };
                    
                    // Include all action-specific parameters from node data
                    if (node.data.type === 'send_email') {
                        baseAction.email = {
                            template: node.data.email?.templateId || node.data.email?.template || null,
                            subject: node.data.email?.subject || '',
                            content: node.data.email?.content || '',
                            recipients: node.data.email?.recipients || [],
                            variables: node.data.email?.variables || {}
                        };
                    } else if (node.data.type === 'send_survey') {
                        // Only include survey if formId is provided
                        if (node.data.survey?.formId) {
                        baseAction.survey = {
                                formId: node.data.survey.formId,
                                recipientType: node.data.survey?.recipientType || 'client',
                                recipientEmail: node.data.survey?.recipientEmail || '',
                                message: node.data.survey?.message || ''
                            };
                        } else {
                            console.warn('Skipping send_survey action - formId is required');
                            return null;
                        }
                    } else if (node.data.type === 'create_task') {
                        baseAction.task = {
                            title: node.data.task?.title || '',
                            category: node.data.task?.category || 'other',
                            priority: node.data.task?.priority || 'medium',
                            description: node.data.task?.description || ''
                        };
                    } else if (node.data.type === 'update_status') {
                        baseAction.statusUpdate = {
                            entity: node.data.statusUpdate?.entity || node.data.status?.entityType || 'client',
                            status: node.data.statusUpdate?.status || node.data.status?.value || '',
                            field: node.data.statusUpdate?.field || 'status',
                            value: node.data.statusUpdate?.value || node.data.status?.value || ''
                        };
                    } else if (node.data.type === 'assign_user') {
                        baseAction.assignment = {
                            entity: node.data.assignment?.entity || node.data.assign?.entityType || 'client',
                            assignedTo: node.data.assignment?.assignedTo || node.data.assign?.userId || '',
                            role: node.data.assignment?.role || ''
                        };
                    } else if (node.data.type === 'delay') {
                        baseAction.delay = {
                            amount: node.data.delay?.amount || 0,
                            unit: node.data.delay?.unit || 'minutes'
                        };
                    } else if (node.data.type === 'webhook_call') {
                        baseAction.webhook = {
                            url: node.data.webhook?.url || '',
                            method: node.data.webhook?.method || 'POST',
                            headers: node.data.webhook?.headers || {},
                            body: node.data.webhook?.body || {}
                        };
                    } else if (node.data.type === 'send_notification') {
                        baseAction.notification = {
                            type: node.data.notification?.type || 'in_app',
                            message: node.data.notification?.message || node.data.notification?.title || '',
                            recipients: node.data.notification?.recipients || []
                        };
                    } else if (node.data.type === 'create_lead') {
                        baseAction.lead = {
                            firstName: node.data.lead?.firstName || '',
                            lastName: node.data.lead?.lastName || '',
                            email: node.data.lead?.email || '',
                            phone: node.data.lead?.phone || '',
                            company: node.data.lead?.company || '',
                            source: node.data.lead?.source || 'web_form',
                            status: node.data.lead?.status || 'new',
                            assignedTo: node.data.lead?.assignedTo || null,
                            tags: node.data.lead?.tags || [],
                            customFields: node.data.lead?.customFields || {}
                        };
                    } else if (node.data.type === 'update_lead') {
                        baseAction.lead = {
                            ...(node.data.lead?.leadId && { leadId: node.data.lead.leadId }),
                            updates: node.data.lead?.updates || {},
                            ...(node.data.lead?.scoreIncrement !== undefined && { scoreIncrement: node.data.lead.scoreIncrement })
                        };
                    } else if (node.data.type === 'condition_check' || node.type === 'condition') {
                        if (!node.data.condition?.field || !node.data.condition?.operator) {
                            console.warn('Skipping condition_check action - field and operator are required');
                            return null;
                        }
                        baseAction.type = 'condition_check';
                        
                        // Find all edges from this condition node and determine which branch they belong to
                        const trueEdges = edges.filter(e => e.source === node.id && e.sourceHandle === 'true');
                        const falseEdges = edges.filter(e => e.source === node.id && e.sourceHandle === 'false');
                        
                        // Get action order numbers for true and false branches
                        // Use the same sorted order as in the actions array (allNodes)
                        const trueActions = trueEdges.map(edge => {
                            const targetNodeIndex = allNodes.findIndex(n => n.id === edge.target);
                            return targetNodeIndex >= 0 ? targetNodeIndex + 1 : null;
                        }).filter(order => order !== null);
                        
                        const falseActions = falseEdges.map(edge => {
                            const targetNodeIndex = allNodes.findIndex(n => n.id === edge.target);
                            return targetNodeIndex >= 0 ? targetNodeIndex + 1 : null;
                        }).filter(order => order !== null);
                        
                        baseAction.condition = {
                            field: node.data.condition.field,
                            operator: node.data.condition.operator,
                            ...(node.data.condition.value && { value: node.data.condition.value }),
                            trueActions: trueActions,
                            falseActions: falseActions
                        };
                    } else if (node.data.type === 'delete_lead') {
                        const leadId = node.data.lead?.leadId;
                        if (leadId) {
                            baseAction.lead = {
                                leadId: leadId
                            };
                        } else {
                            console.warn('Skipping delete_lead action - leadId is required');
                            return null;
                        }
                    } else if (node.data.type === 'convert_lead') {
                        const leadId = node.data.lead?.leadId;
                        if (leadId) {
                            baseAction.lead = {
                                leadId: leadId,
                                convertToClient: node.data.lead?.convertToClient || false,
                                clientData: node.data.lead?.clientData || {}
                            };
                        } else {
                            console.warn('Skipping convert_lead action - leadId is required');
                            return null;
                        }
                    } else if (node.data.type === 'create_opportunity') {
                        baseAction.opportunity = {
                            name: node.data.opportunity?.name || '',
                            value: node.data.opportunity?.value || 0,
                            stage: node.data.opportunity?.stage || 'prospecting',
                            probability: node.data.opportunity?.probability || 10,
                            type: node.data.opportunity?.type || 'new_business',
                            description: node.data.opportunity?.description || '',
                            closeDate: node.data.opportunity?.closeDate || null,
                            accountId: node.data.opportunity?.accountId || null,
                            linkedClientId: node.data.opportunity?.linkedClientId || null
                        };
                    } else if (node.data.type === 'update_opportunity') {
                        const opportunityId = node.data.opportunity?.opportunityId;
                        if (opportunityId) {
                            baseAction.opportunity = {
                                opportunityId: opportunityId,
                                updates: node.data.opportunity?.updates || {}
                            };
                        } else {
                            console.warn('Skipping update_opportunity action - opportunityId is required');
                            return null;
                        }
                    } else if (node.data.type === 'delete_opportunity') {
                        const opportunityId = node.data.opportunity?.opportunityId;
                        if (opportunityId) {
                            baseAction.opportunity = {
                                opportunityId: opportunityId
                            };
                        } else {
                            console.warn('Skipping delete_opportunity action - opportunityId is required');
                            return null;
                        }
                    } else if (node.data.type === 'update_entity_params') {
                        // Only include entityUpdate if all required fields are present
                        const entity = node.data.entityUpdate?.entity;
                        const entityId = node.data.entityUpdate?.entityId;
                        let updates = node.data.entityUpdate?.updates;
                        
                        // Handle updates if it's a string (JSON) - parse it
                        if (typeof updates === 'string' && updates.trim()) {
                            try {
                                updates = JSON.parse(updates);
                            } catch (e) {
                                console.warn('Invalid JSON in updates field:', e);
                                updates = null;
                            }
                        }
                        
                        // Validate all required fields are present and valid
                        if (entity && entity.trim() && 
                            entityId && entityId.trim() && 
                            updates && 
                            typeof updates === 'object' && 
                            Object.keys(updates).length > 0) {
                            baseAction.entityUpdate = {
                                entity: entity.trim(),
                                entityId: entityId.trim(),
                                updates: updates
                            };
                        } else {
                            // Skip this action if required fields are missing
                            console.warn('Skipping update_entity_params action - missing required fields:', {
                                entity: !!entity,
                                entityId: !!entityId,
                                updates: !!updates,
                                updatesType: typeof updates,
                                updatesKeys: updates && typeof updates === 'object' ? Object.keys(updates).length : 0
                            });
                            return null;
                        }
                    }
                    return baseAction;
                }).filter(action => {
                    // Double-check: filter out any actions that shouldn't be sent
                    if (action === null) return false;
                    
                    // Additional safety check for update_entity_params
                    if (action.type === 'update_entity_params') {
                        const hasEntity = action.entityUpdate?.entity && action.entityUpdate.entity.trim();
                        const hasEntityId = action.entityUpdate?.entityId && action.entityUpdate.entityId.trim();
                        const hasUpdates = action.entityUpdate?.updates && 
                            typeof action.entityUpdate.updates === 'object' && 
                            Object.keys(action.entityUpdate.updates).length > 0;
                        
                        if (!hasEntity || !hasEntityId || !hasUpdates) {
                            console.warn('Filtering out incomplete update_entity_params action at final check');
                            return false;
                        }
                    }
                    
                    return true;
                }),
                settings: {
                    enabled: true,
                    runOnce: false
                },
                visualData: {
                    nodes,
                    edges
                }
            };

            console.log('Submitting visual workflow data:', workflowData);
            console.log('Actions being sent:', workflowData.actions);
            console.log('Action types:', workflowData.actions.map(a => a.type));
            
            // Final safety check: completely remove any update_entity_params actions that are incomplete
            workflowData.actions = workflowData.actions.filter(action => {
                if (action.type === 'update_entity_params') {
                    const hasEntity = action.entityUpdate?.entity && action.entityUpdate.entity.trim();
                    const hasEntityId = action.entityUpdate?.entityId && action.entityUpdate.entityId.trim();
                    const hasUpdates = action.entityUpdate?.updates && 
                        typeof action.entityUpdate.updates === 'object' && 
                        Object.keys(action.entityUpdate.updates).length > 0;
                    
                    if (!hasEntity || !hasEntityId || !hasUpdates) {
                        console.warn('Removing incomplete update_entity_params action before sending:', action);
                        return false;
                    }
                }
                return true;
            });
            
            // Re-number actions after filtering
            workflowData.actions = workflowData.actions.map((action, index) => ({
                ...action,
                order: index + 1
            }));
            
            console.log('Final actions after filtering:', workflowData.actions.map(a => ({ type: a.type, order: a.order })));
            
            let response;
            
            if (isGlobalTemplate) {
                // Save as global template
                const slug = workflowName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                const globalTemplateData = {
                    type: 'workflow',
                    slug: slug,
                    title: workflowName,
                    description: workflowDescription || '',
                    category: '',
                    tags: [],
                    content: {
                        trigger: workflowData.trigger,
                        actions: workflowData.actions,
                        settings: workflowData.settings,
                        visualData: workflowData.visualData
                    }
                };
                
                if (workflowId) {
                    response = await adminSystemAPI.updateTemplate(workflowId, globalTemplateData);
                    if (response.data?.success) {
                        toast.success('Global template updated successfully!');
                        if (window.opener) {
                            window.close();
                        } else {
                            navigate('/admin?tab=templates');
                        }
                    } else {
                        toast.error(response.data?.message || 'Failed to update global template');
                    }
                } else {
                    response = await adminSystemAPI.createTemplate(globalTemplateData);
                    if (response.data?.success) {
                        toast.success('Global template created successfully!');
                        if (window.opener) {
                            window.close();
                        } else {
                            navigate('/admin?tab=templates');
                        }
                    } else {
                        toast.error(response.data?.message || 'Failed to create global template');
                    }
                }
            } else {
                // Save as regular agency workflow
                if (workflowId) {
                    // Update existing workflow
                    response = await workflowAPI.update(workflowId, workflowData);
                    if (response.data?.success) {
                        toast.success('Workflow updated successfully!');
                        navigate('/workflows');
                    } else {
                        toast.error(response.data?.message || 'Failed to update workflow');
                    }
                } else {
                    // Create new workflow
                    response = await workflowAPI.create(workflowData);
                    if (response.data?.success) {
                        toast.success('Workflow created successfully!');
                        navigate('/workflows');
                    } else {
                        toast.error(response.data?.message || 'Failed to create workflow');
                    }
                }
            }
        } catch (error) {
            console.error('Error creating workflow:', error);
            const errorData = error.response?.data;
            console.error('Error details:', errorData);
            
            // Show detailed validation errors if available
            if (errorData?.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
                const errorMessages = errorData.errors.map(err => `${err.field}: ${err.message}`).join(', ');
                toast.error(`Validation errors: ${errorMessages}`, { duration: 5000 });
            } else {
                const errorMessage = errorData?.message || error.message || 'Failed to create workflow';
                toast.error(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setNodes([]);
        setEdges([]);
    };

    const handleDeleteNode = (nodeId) => {
        setNodes(nodes.filter(node => node.id !== nodeId));
        setEdges(edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId));
        if (selectedNode?.id === nodeId) {
            setSelectedNode(null);
        }
    };
    
    const handleNodeClick = (event, node) => {
        setSelectedNode(node);
    };
    
    const updateNodeData = (nodeId, newData) => {
        setNodes(nodes.map(node => 
            node.id === nodeId 
                ? { ...node, data: { ...node.data, ...newData } }
                : node
        ));
        if (selectedNode?.id === nodeId) {
            setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, ...newData } });
        }
    };
    
    const updateNodeNested = (nodeId, section, field, value) => {
        setNodes(nodes.map(node => 
            node.id === nodeId 
                ? { 
                    ...node, 
                    data: { 
                        ...node.data, 
                        [section]: { ...node.data[section], [field]: value }
                    } 
                }
                : node
        ));
        if (selectedNode?.id === nodeId) {
            setSelectedNode({ 
                ...selectedNode, 
                data: { 
                    ...selectedNode.data, 
                    [section]: { ...selectedNode.data[section], [field]: value }
                } 
            });
        }
    };

    const handleLayout = useCallback(() => {
        if (nodes.length === 0) {
            toast.error('No nodes to layout');
            return;
        }

        const { nodes: layoutedNodes } = getLayoutedElements([...nodes], edges);
        setNodes(layoutedNodes);
        
        // Auto-fit view after layout is applied
        setTimeout(() => {
            // This will be handled by the fitView effect in WorkflowCanvas
        }, 100);
        
        toast.success('Layout applied');
    }, [nodes, edges, setNodes]);

    return (
        <ReactFlowProvider>
            <div className="h-screen flex flex-col">
                {/* Header */}
                <div className="bg-card border-b border-border px-6 py-4">
                    {/* Helper tip for edge deletion */}
                    <div className="mb-2 text-xs text-muted-foreground">
                        💡 Tip: Click on a connection line and press <kbd className="px-1.5 py-0.5 bg-muted border border-input rounded text-xs">Delete</kbd> or <kbd className="px-1.5 py-0.5 bg-muted border border-input rounded text-xs">Backspace</kbd> to remove it
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => {
                                    if (isGlobalTemplate) {
                                        if (window.opener) {
                                            window.close();
                                        } else {
                                            navigate('/admin?tab=templates');
                                        }
                                    } else {
                                        navigate('/workflows');
                                    }
                                }}
                                className="p-2 hover:bg-muted rounded-lg"
                            >
                                <ArrowLeftIcon className="h-5 w-5" />
                            </button>
                            <div>
                                <h1 className="text-xl font-semibold text-foreground">
                                    {isGlobalTemplate ? 'Global Template - Visual Workflow Builder' : 'Visual Workflow Builder'}
                                </h1>
                                <p className="text-sm text-muted-foreground">
                                    {isGlobalTemplate 
                                        ? 'Creating a global workflow template that will be available to all agencies'
                                        : 'Drag and drop to build your workflow'
                                    }
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleClear}
                                className="px-3 py-2 text-sm border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted"
                            >
                                Clear
                            </button>
                            <button
                                onClick={handleLayout}
                                className="px-3 py-2 text-sm border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted flex items-center gap-2"
                                title="Auto-layout workflow"
                            >
                                <Squares2X2Icon className="h-4 w-4" />
                                Auto Layout
                            </button>
                            <button
                                onClick={() => fitViewRef.current?.()}
                                className="px-3 py-2 text-sm border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md hover:bg-muted flex items-center gap-2"
                                title="Fit all nodes in view"
                            >
                                <EyeIcon className="h-4 w-4" />
                                Fit to View
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={loading || loadingWorkflow}
                                className="px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50"
                                style={{ backgroundColor: branding?.primaryColor }}
                            >
                                {loading ? 'Saving...' : loadingWorkflow ? 'Loading...' : workflowId ? 'Update Workflow' : 'Save Workflow'}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 flex">
                    {/* Node Palette */}
                    <div className="w-64 bg-muted border-r border-border p-4 overflow-y-auto">
                        <h3 className="font-semibold text-foreground mb-4">Workflow Elements</h3>
                        
                        {/* Basic Info */}
                        <div className="mb-6 space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Name</label>
                                <input
                                    type="text"
                                    value={workflowName}
                                    onChange={(e) => setWorkflowName(e.target.value)}
                                    placeholder="Workflow name"
                                    className="w-full text-sm border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md px-2 py-1"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                                <textarea
                                    value={workflowDescription}
                                    onChange={(e) => setWorkflowDescription(e.target.value)}
                                    placeholder="Workflow description"
                                    rows={2}
                                    className="w-full text-sm border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-md px-2 py-1"
                                />
                            </div>
                        </div>

                        {/* Triggers */}
                        <div className="mb-4">
                            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                                <UserGroupIcon className="h-4 w-4" />
                                Triggers
                            </h4>
                            <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                {/* Client Triggers */}
                                {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'client').length > 0 && (
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase">Client</div>
                            <div className="space-y-1">
                                            {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'client').map((node, index) => (
                                    <div
                                        key={index}
                                        className="p-2 bg-card border border-border rounded-md cursor-move hover:bg-muted transition-colors"
                                        draggable
                                        onDragStart={(event) => onDragStart(event, node)}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <div className="text-xs font-medium">{node.label}</div>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{node.description}</div>
                                    </div>
                                ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Task Triggers */}
                                {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'task').length > 0 && (
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase">Task</div>
                                        <div className="space-y-1">
                                            {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'task').map((node, index) => (
                                                <div
                                                    key={index}
                                                    className="p-2 bg-card border border-border rounded-md cursor-move hover:bg-muted transition-colors"
                                                    draggable
                                                    onDragStart={(event) => onDragStart(event, node)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                        <div className="text-xs font-medium">{node.label}</div>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-1">{node.description}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Lead Triggers */}
                                {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'lead').length > 0 && (
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase">Lead</div>
                                        <div className="space-y-1">
                                            {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'lead').map((node, index) => (
                                                <div
                                                    key={index}
                                                    className="p-2 bg-card border border-border rounded-md cursor-move hover:bg-muted transition-colors"
                                                    draggable
                                                    onDragStart={(event) => onDragStart(event, node)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                        <div className="text-xs font-medium">{node.label}</div>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-1">{node.description}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Email Triggers */}
                                {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'email').length > 0 && (
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase">Email Activity</div>
                                        <div className="space-y-1">
                                            {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'email').map((node, index) => (
                                                <div
                                                    key={index}
                                                    className="p-2 bg-card border border-border rounded-md cursor-move hover:bg-muted transition-colors"
                                                    draggable
                                                    onDragStart={(event) => onDragStart(event, node)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                        <div className="text-xs font-medium">{node.label}</div>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-1">{node.description}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Form Triggers */}
                                {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'form').length > 0 && (
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase">Form</div>
                                        <div className="space-y-1">
                                            {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'form').map((node, index) => (
                                                <div
                                                    key={index}
                                                    className="p-2 bg-card border border-border rounded-md cursor-move hover:bg-muted transition-colors"
                                                    draggable
                                                    onDragStart={(event) => onDragStart(event, node)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                        <div className="text-xs font-medium">{node.label}</div>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-1">{node.description}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Website Triggers */}
                                {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'website').length > 0 && (
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase">Website</div>
                                        <div className="space-y-1">
                                            {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'website').map((node, index) => (
                                                <div
                                                    key={index}
                                                    className="p-2 bg-card border border-border rounded-md cursor-move hover:bg-muted transition-colors"
                                                    draggable
                                                    onDragStart={(event) => onDragStart(event, node)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                        <div className="text-xs font-medium">{node.label}</div>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-1">{node.description}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Opportunity Triggers */}
                                {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'opportunity').length > 0 && (
                                    <div>
                                        <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase">Opportunity</div>
                                        <div className="space-y-1">
                                            {nodePalette.filter(node => node.type === 'trigger' && node.data.category === 'opportunity').map((node, index) => (
                                                <div
                                                    key={index}
                                                    className="p-2 bg-card border border-border rounded-md cursor-move hover:bg-muted transition-colors"
                                                    draggable
                                                    onDragStart={(event) => onDragStart(event, node)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                                        <div className="text-xs font-medium">{node.label}</div>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-1">{node.description}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mb-4">
                            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                                <CogIcon className="h-4 w-4" />
                                Actions
                            </h4>
                            <div className="space-y-1">
                                {nodePalette.filter(node => node.type === 'action').map((node, index) => (
                                    <div
                                        key={index}
                                        className="p-2 bg-card border border-border rounded-md cursor-move hover:bg-muted transition-colors"
                                        draggable
                                        onDragStart={(event) => onDragStart(event, node)}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${node.data.type === 'email' ? 'bg-blue-500' : 'bg-yellow-500'}`}></div>
                                            <div className="text-xs font-medium">{node.label}</div>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{node.description}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Conditions */}
                        <div>
                            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                Conditions
                            </h4>
                            <div className="space-y-1">
                                {nodePalette.filter(node => node.type === 'condition').map((node, index) => (
                                    <div
                                        key={index}
                                        className="p-2 bg-card border border-border rounded-md cursor-move hover:bg-muted transition-colors"
                                        draggable
                                        onDragStart={(event) => onDragStart(event, node)}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                            <div className="text-xs font-medium">{node.label}</div>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{node.description}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Workflow Canvas */}
                    <div className="flex-1 relative">
                        <WorkflowCanvas
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            nodeTypes={nodeTypes}
                            branding={branding}
                            setEdges={setEdges}
                            onNodeClick={handleNodeClick}
                            lastAddedNodeId={lastAddedNodeId}
                            onFitView={(fn) => { fitViewRef.current = fn; }}
                        />
                    </div>
                    
                    {/* Node Configuration Sidebar */}
                    {selectedNode && (
                        <div className="w-80 bg-card border-l border-border p-4 overflow-y-auto">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold text-foreground">Configure Node</h3>
                                <button
                                    onClick={() => setSelectedNode(null)}
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    ×
                                </button>
                            </div>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Node Label
                                    </label>
                                    <input
                                        type="text"
                                        value={selectedNode.data.label || ''}
                                        onChange={(e) => updateNodeData(selectedNode.id, { label: e.target.value })}
                                        className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                    />
                                </div>
                                
                                {/* Trigger-specific parameter fields */}
                                {selectedNode.type === 'trigger' && selectedNode.data.category === 'email' && (
                                    <div className="space-y-4">
                                        <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">Email Filter</h4>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Email Template (optional)
                                            </label>
                                            <select
                                                value={selectedNode.data.eventFilters?.emailTemplateId || ''}
                                                onChange={(e) => {
                                                    const eventFilters = { ...selectedNode.data.eventFilters };
                                                    if (e.target.value) {
                                                        eventFilters.emailTemplateId = e.target.value;
                                                    } else {
                                                        delete eventFilters.emailTemplateId;
                                                    }
                                                    updateNodeData(selectedNode.id, { eventFilters });
                                                }}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">Any email template</option>
                                                {emailTemplatesLoading ? (
                                                    <option disabled>Loading templates...</option>
                                                ) : emailTemplates.length === 0 ? (
                                                    <option disabled>No email templates available</option>
                                                ) : (
                                                    emailTemplates.map(template => (
                                                        <option key={template._id} value={template._id}>
                                                            {template.name}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Email Subject Contains (optional)
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedNode.data.eventFilters?.emailSubject || ''}
                                                onChange={(e) => {
                                                    const eventFilters = { ...selectedNode.data.eventFilters };
                                                    if (e.target.value) {
                                                        eventFilters.emailSubject = e.target.value;
                                                    } else {
                                                        delete eventFilters.emailSubject;
                                                    }
                                                    updateNodeData(selectedNode.id, { eventFilters });
                                                }}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Filter by email subject"
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'trigger' && selectedNode.data.category === 'form' && (
                                    <div className="space-y-4">
                                        <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">Form Filter</h4>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Form (optional)
                                            </label>
                                            <select
                                                value={selectedNode.data.eventFilters?.formId || ''}
                                                onChange={(e) => {
                                                    const eventFilters = { ...selectedNode.data.eventFilters };
                                                    if (e.target.value) {
                                                        eventFilters.formId = e.target.value;
                                                    } else {
                                                        delete eventFilters.formId;
                                                    }
                                                    updateNodeData(selectedNode.id, { eventFilters });
                                                }}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">Any form</option>
                                                {formsLoading ? (
                                                    <option disabled>Loading forms...</option>
                                                ) : forms.length === 0 ? (
                                                    <option disabled>No forms available</option>
                                                ) : (
                                                    forms.map(form => (
                                                        <option key={form._id} value={form._id}>
                                                            {form.name}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'trigger' && selectedNode.data.category === 'website' && (
                                    <div className="space-y-4">
                                        <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">URL Filter</h4>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                URL Pattern (optional)
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedNode.data.eventFilters?.urlPattern || ''}
                                                onChange={(e) => {
                                                    const eventFilters = { ...selectedNode.data.eventFilters };
                                                    if (e.target.value) {
                                                        eventFilters.urlPattern = e.target.value;
                                                    } else {
                                                        delete eventFilters.urlPattern;
                                                    }
                                                    updateNodeData(selectedNode.id, { eventFilters });
                                                }}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="e.g., /contact, /pricing, or regex pattern"
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Leave empty to trigger on any URL
                                            </p>
                                        </div>
                                        {selectedNode.data.type === 'link_clicked' && (
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Link Text Contains (optional)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={selectedNode.data.eventFilters?.linkText || ''}
                                                    onChange={(e) => {
                                                        const eventFilters = { ...selectedNode.data.eventFilters };
                                                        if (e.target.value) {
                                                            eventFilters.linkText = e.target.value;
                                                        } else {
                                                            delete eventFilters.linkText;
                                                        }
                                                        updateNodeData(selectedNode.id, { eventFilters });
                                                    }}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="Filter by link text"
                                                />
                                            </div>
                                        )}
                                        {selectedNode.data.type === 'resource_downloaded' && (
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Resource Type (optional)
                                                </label>
                                                <select
                                                    value={selectedNode.data.eventFilters?.resourceType || ''}
                                                    onChange={(e) => {
                                                        const eventFilters = { ...selectedNode.data.eventFilters };
                                                        if (e.target.value) {
                                                            eventFilters.resourceType = e.target.value;
                                                        } else {
                                                            delete eventFilters.resourceType;
                                                        }
                                                        updateNodeData(selectedNode.id, { eventFilters });
                                                    }}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="">Any resource type</option>
                                                    <option value="pdf">PDF</option>
                                                    <option value="doc">Document</option>
                                                    <option value="docx">Word Document</option>
                                                    <option value="xls">Excel</option>
                                                    <option value="image">Image</option>
                                                    <option value="video">Video</option>
                                                    <option value="zip">Archive</option>
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {/* Action-specific parameter fields */}
                                {selectedNode.type === 'action' && selectedNode.data.type === 'send_email' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Email Template
                                            </label>
                                            <select
                                                value={selectedNode.data.email?.templateId || ''}
                                                onChange={(e) => {
                                                    const templateId = e.target.value;
                                                    const template = emailTemplates.find(t => t._id === templateId);
                                                    updateNodeNested(selectedNode.id, 'email', 'templateId', templateId);
                                                    if (template) {
                                                        updateNodeNested(selectedNode.id, 'email', 'subject', template.subject || '');
                                                        updateNodeNested(selectedNode.id, 'email', 'content', template.content || '');
                                                    }
                                                }}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">Select a template or enter manually...</option>
                                                {emailTemplatesLoading ? (
                                                    <option disabled>Loading templates...</option>
                                                ) : emailTemplates.length === 0 ? (
                                                    <option disabled>No email templates available</option>
                                                ) : (
                                                    emailTemplates.map(template => (
                                                        <option key={template._id} value={template._id}>
                                                            {template.name}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Email Subject
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedNode.data.email?.subject || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'email', 'subject', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Email subject"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Email Content
                                            </label>
                                            <textarea
                                                value={selectedNode.data.email?.content || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'email', 'content', e.target.value)}
                                                rows={4}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Email content (HTML supported)"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Recipients
                                            </label>
                                            <select
                                                value={selectedNode.data.email?.recipientType || 'client'}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'email', 'recipientType', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="client">Client (from trigger)</option>
                                                <option value="lead">Lead (from trigger)</option>
                                                <option value="assigned_user">Assigned User</option>
                                                <option value="custom">Custom Email</option>
                                            </select>
                                        </div>
                                        {selectedNode.data.email?.recipientType === 'custom' && (
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Custom Email Address
                                                </label>
                                                <input
                                                    type="email"
                                                    value={selectedNode.data.email?.customEmail || ''}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'email', 'customEmail', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="recipient@example.com"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'create_task' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Task Title *
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedNode.data.task?.title || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'task', 'title', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Task title"
                                                required
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Category
                                                </label>
                                                <select
                                                    value={selectedNode.data.task?.category || 'other'}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'task', 'category', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="visa">Visa</option>
                                                    <option value="housing">Housing</option>
                                                    <option value="banking">Banking</option>
                                                    <option value="healthcare">Healthcare</option>
                                                    <option value="education">Education</option>
                                                    <option value="employment">Employment</option>
                                                    <option value="legal">Legal</option>
                                                    <option value="immigration">Immigration</option>
                                                    <option value="relocation">Relocation</option>
                                                    <option value="documentation">Documentation</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Priority
                                                </label>
                                                <select
                                                    value={selectedNode.data.task?.priority || 'medium'}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'task', 'priority', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="low">Low</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="high">High</option>
                                                    <option value="urgent">Urgent</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Description
                                            </label>
                                            <textarea
                                                value={selectedNode.data.task?.description || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'task', 'description', e.target.value)}
                                                rows={3}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Task description"
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'update_status' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Entity Type
                                            </label>
                                            <select
                                                value={selectedNode.data.status?.entityType || 'client'}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'status', 'entityType', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="client">Client</option>
                                                <option value="lead">Lead</option>
                                                <option value="task">Task</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                New Status *
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedNode.data.status?.value || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'status', 'value', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="e.g., active, qualified, completed"
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'assign_user' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Entity Type *
                                            </label>
                                            <select
                                                value={selectedNode.data.assignment?.entity || 'client'}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'assignment', 'entity', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="client">Client</option>
                                                <option value="lead">Lead</option>
                                                <option value="task">Task</option>
                                                <option value="account">Account</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Assign To *
                                            </label>
                                            <select
                                                value={selectedNode.data.assignment?.assignedTo || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'assignment', 'assignedTo', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            >
                                                <option value="">Select a user...</option>
                                                {usersLoading ? (
                                                    <option disabled>Loading users...</option>
                                                ) : users.length === 0 ? (
                                                    <option disabled>No users available</option>
                                                ) : (
                                                    users.map(user => (
                                                        <option key={user._id} value={user._id}>
                                                            {user.name || user.email}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'delay' && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Delay Amount *
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={selectedNode.data.delay?.amount || ''}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'delay', 'amount', parseInt(e.target.value))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Delay Unit *
                                                </label>
                                                <select
                                                    value={selectedNode.data.delay?.unit || 'minutes'}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'delay', 'unit', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="minutes">Minutes</option>
                                                    <option value="hours">Hours</option>
                                                    <option value="days">Days</option>
                                                    <option value="weeks">Weeks</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'webhook_call' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Webhook URL *
                                            </label>
                                            <input
                                                type="url"
                                                value={selectedNode.data.webhook?.url || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'webhook', 'url', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="https://example.com/webhook"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                HTTP Method
                                            </label>
                                            <select
                                                value={selectedNode.data.webhook?.method || 'POST'}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'webhook', 'method', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="POST">POST</option>
                                                <option value="GET">GET</option>
                                                <option value="PUT">PUT</option>
                                                <option value="PATCH">PATCH</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'send_notification' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Notification Title *
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedNode.data.notification?.title || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'notification', 'title', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Notification title"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Notification Message *
                                            </label>
                                            <textarea
                                                value={selectedNode.data.notification?.message || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'notification', 'message', e.target.value)}
                                                rows={3}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Notification message"
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'send_survey' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Survey Form *
                                            </label>
                                            <select
                                                value={selectedNode.data.survey?.formId || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'survey', 'formId', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            >
                                                <option value="">Select a form...</option>
                                                {formsLoading ? (
                                                    <option disabled>Loading forms...</option>
                                                ) : forms.length === 0 ? (
                                                    <option disabled>No published forms available</option>
                                                ) : (
                                                    forms.map(form => (
                                                        <option key={form._id} value={form._id}>
                                                            {form.name}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Send To
                                            </label>
                                            <select
                                                value={selectedNode.data.survey?.recipientType || 'client'}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'survey', 'recipientType', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="client">Client (from trigger)</option>
                                                <option value="lead">Lead (from trigger)</option>
                                                <option value="email">Custom Email</option>
                                            </select>
                                        </div>
                                        {selectedNode.data.survey?.recipientType === 'email' && (
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Email Address *
                                                </label>
                                                <input
                                                    type="email"
                                                    value={selectedNode.data.survey?.recipientEmail || ''}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'survey', 'recipientEmail', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="recipient@example.com"
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Custom Message (optional)
                                            </label>
                                            <textarea
                                                value={selectedNode.data.survey?.message || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'survey', 'message', e.target.value)}
                                                rows={3}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Add a personalized message to include in the survey invite email..."
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'create_lead' && (
                                    <div className="space-y-4">
                                        {/* Variable Helper */}
                                        <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                            <p className="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-2">💡 Use Variables</p>
                                            <p className="text-xs text-blue-800 dark:text-blue-300 mb-2">
                                                You can use variables from form submissions or previous actions:
                                            </p>
                                            <div className="space-y-1 text-xs text-blue-700 dark:text-blue-400 font-mono">
                                                <div><code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{"{{form.email}}"}</code> - Email from form</div>
                                                <div><code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{"{{form.firstName}}"}</code> - First name from form</div>
                                                <div><code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{"{{action1.email}}"}</code> - Email from action 1</div>
                                                <div><code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">{"{{answers.email}}"}</code> - Any form field</div>
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    First Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={selectedNode.data.lead?.firstName || ''}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'firstName', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="John or {{form.firstName}}"
                                                />
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Auto-extracted from form if empty
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Last Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={selectedNode.data.lead?.lastName || ''}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'lastName', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="Doe or {{form.lastName}}"
                                                />
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Auto-extracted from form if empty
                                                </p>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Email *
                                            </label>
                                            <input
                                                type="email"
                                                value={selectedNode.data.lead?.email || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'email', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="email@example.com or {{form.email}}"
                                                required
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Auto-extracted from form if empty. Use {"{{form.email}}"} to reference form submission.
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Source
                                                </label>
                                                <select
                                                    value={selectedNode.data.lead?.source || 'web_form'}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'source', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="web_form">Web Form</option>
                                                    <option value="referral">Referral</option>
                                                    <option value="social_media">Social Media</option>
                                                    <option value="email_campaign">Email Campaign</option>
                                                    <option value="phone_call">Phone Call</option>
                                                    <option value="website">Website</option>
                                                    <option value="search">Search</option>
                                                    <option value="advertisement">Advertisement</option>
                                                    <option value="event">Event</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Status
                                                </label>
                                                <select
                                                    value={selectedNode.data.lead?.status || 'new'}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'status', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="new">New</option>
                                                    <option value="contacted">Contacted</option>
                                                    <option value="qualified">Qualified</option>
                                                    <option value="nurturing">Nurturing</option>
                                                    <option value="converted">Converted</option>
                                                    <option value="disqualified">Disqualified</option>
                                                    <option value="lost">Lost</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Assign To
                                            </label>
                                            <select
                                                value={selectedNode.data.lead?.assignedTo || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'assignedTo', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">Unassigned</option>
                                                {usersLoading ? (
                                                    <option disabled>Loading users...</option>
                                                ) : users.length === 0 ? (
                                                    <option disabled>No users available</option>
                                                ) : (
                                                    users.map(user => (
                                                        <option key={user._id} value={user._id}>
                                                            {user.name || user.email}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'update_lead' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Lead (optional - will find by email if not provided)
                                            </label>
                                            <select
                                                value={selectedNode.data.lead?.leadId || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'leadId', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">Auto-detect from form email</option>
                                                {leadsLoading ? (
                                                    <option disabled>Loading leads...</option>
                                                ) : leads.length === 0 ? (
                                                    <option disabled>No leads available</option>
                                                ) : (
                                                    leads.map(lead => (
                                                        <option key={lead._id} value={lead._id}>
                                                            {lead.firstName} {lead.lastName} ({lead.email})
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                If not specified, will find lead by email from form submission
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Status
                                            </label>
                                            <select
                                                value={selectedNode.data.lead?.updates?.status || ''}
                                                onChange={(e) => {
                                                    const updates = { ...selectedNode.data.lead?.updates, status: e.target.value || undefined };
                                                    updateNodeNested(selectedNode.id, 'lead', 'updates', updates);
                                                }}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">No change</option>
                                                <option value="new">New</option>
                                                <option value="contacted">Contacted</option>
                                                <option value="qualified">Qualified</option>
                                                <option value="nurturing">Nurturing</option>
                                                <option value="converted">Converted</option>
                                                <option value="disqualified">Disqualified</option>
                                                <option value="lost">Lost</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Score Increment
                                            </label>
                                            <input
                                                type="number"
                                                min="-100"
                                                max="100"
                                                value={selectedNode.data.lead?.scoreIncrement || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'scoreIncrement', e.target.value ? parseInt(e.target.value) : undefined)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="e.g., +5 or -2"
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Add or subtract from current lead score (will also update engagement)
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Assign To
                                            </label>
                                            <select
                                                value={selectedNode.data.lead?.updates?.assignedTo || ''}
                                                onChange={(e) => {
                                                    const updates = { ...selectedNode.data.lead?.updates, assignedTo: e.target.value || undefined };
                                                    updateNodeNested(selectedNode.id, 'lead', 'updates', updates);
                                                }}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                            >
                                                <option value="">No change</option>
                                                {usersLoading ? (
                                                    <option disabled>Loading users...</option>
                                                ) : users.length === 0 ? (
                                                    <option disabled>No users available</option>
                                                ) : (
                                                    users.map(user => (
                                                        <option key={user._id} value={user._id}>
                                                            {user.name || user.email}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                )}
                                
                                {(selectedNode.type === 'condition' || (selectedNode.type === 'action' && selectedNode.data.type === 'condition_check')) && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Condition Field *
                                            </label>
                                            <select
                                                value={selectedNode.data.condition?.field || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'condition', 'field', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            >
                                                <option value="">Select condition...</option>
                                                <option value="lead_exists">Lead Exists (by email)</option>
                                                <option value="leadExists">Lead Exists (alternative)</option>
                                                <option value="form.email">Form Email</option>
                                                <option value="answers.email">Answers Email</option>
                                            </select>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Use "Lead Exists" to check if a lead with the form email already exists
                                            </p>
                                        </div>
                                        {selectedNode.data.condition?.field && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-foreground mb-1">
                                                        Operator *
                                                    </label>
                                                    <select
                                                        value={selectedNode.data.condition?.operator || ''}
                                                        onChange={(e) => updateNodeNested(selectedNode.id, 'condition', 'operator', e.target.value)}
                                                        className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                        style={{ '--tw-ring-color': branding.primaryColor }}
                                                        required
                                                    >
                                                        <option value="">Select operator...</option>
                                                        {(selectedNode.data.condition?.field === 'lead_exists' || selectedNode.data.condition?.field === 'leadExists') ? (
                                                            <>
                                                                <option value="equals">Equals (true)</option>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <option value="equals">Equals</option>
                                                                <option value="not_equals">Not Equals</option>
                                                                <option value="contains">Contains</option>
                                                                <option value="not_contains">Not Contains</option>
                                                                <option value="greater_than">Greater Than</option>
                                                                <option value="less_than">Less Than</option>
                                                                <option value="is_empty">Is Empty</option>
                                                                <option value="is_not_empty">Is Not Empty</option>
                                                            </>
                                                        )}
                                                    </select>
                                                </div>
                                                {selectedNode.data.condition?.field !== 'lead_exists' && selectedNode.data.condition?.field !== 'leadExists' && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-foreground mb-1">
                                                            Value
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={selectedNode.data.condition?.value || ''}
                                                            onChange={(e) => updateNodeNested(selectedNode.id, 'condition', 'value', e.target.value)}
                                                            className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                            style={{ '--tw-ring-color': branding.primaryColor }}
                                                            placeholder="Value to compare or {{variable}}"
                                                        />
                                                    </div>
                                                )}
                                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                    <p className="text-sm text-blue-800 font-medium mb-2">
                                                        Visual Connections
                                                    </p>
                                                    <p className="text-xs text-blue-700">
                                                        Connect action nodes to the <span className="font-semibold text-green-600">True</span> or <span className="font-semibold text-red-600">False</span> output handles on this condition node. The workflow will automatically route to the correct branch based on the condition result.
                                                    </p>
                                                    <div className="mt-2 text-xs text-blue-600">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                                            <span>True branch: {edges.filter(e => e.source === selectedNode.id && e.sourceHandle === 'true').length} connection(s)</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                                            <span>False branch: {edges.filter(e => e.source === selectedNode.id && e.sourceHandle === 'false').length} connection(s)</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'delete_lead' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Lead *
                                            </label>
                                            <select
                                                value={selectedNode.data.lead?.leadId || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'leadId', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            >
                                                <option value="">Select a lead...</option>
                                                {leadsLoading ? (
                                                    <option disabled>Loading leads...</option>
                                                ) : leads.length === 0 ? (
                                                    <option disabled>No leads available</option>
                                                ) : (
                                                    leads.map(lead => (
                                                        <option key={lead._id} value={lead._id}>
                                                            {lead.firstName} {lead.lastName} ({lead.email})
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <p className="text-sm text-yellow-800">
                                                ⚠️ This will permanently delete the selected lead. This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'convert_lead' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Lead *
                                            </label>
                                            <select
                                                value={selectedNode.data.lead?.leadId || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'leadId', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            >
                                                <option value="">Select a lead...</option>
                                                {leadsLoading ? (
                                                    <option disabled>Loading leads...</option>
                                                ) : leads.length === 0 ? (
                                                    <option disabled>No leads available</option>
                                                ) : (
                                                    leads.map(lead => (
                                                        <option key={lead._id} value={lead._id}>
                                                            {lead.firstName} {lead.lastName} ({lead.email})
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={selectedNode.data.lead?.convertToClient || false}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'lead', 'convertToClient', e.target.checked)}
                                                className="h-4 w-4 border-input rounded focus:ring-2"
                                                style={{ 
                                                    accentColor: branding.primaryColor,
                                                    '--tw-ring-color': branding.primaryColor
                                                }}
                                            />
                                            <label className="ml-2 text-sm text-foreground">
                                                Convert to client automatically
                                            </label>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'create_opportunity' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Opportunity Name *
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedNode.data.opportunity?.name || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'opportunity', 'name', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Opportunity name"
                                                required
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Value
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={selectedNode.data.opportunity?.value || ''}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'opportunity', 'value', parseFloat(e.target.value))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="0.00"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Stage
                                                </label>
                                                <select
                                                    value={selectedNode.data.opportunity?.stage || 'prospecting'}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'opportunity', 'stage', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="prospecting">Prospecting</option>
                                                    <option value="qualification">Qualification</option>
                                                    <option value="proposal">Proposal</option>
                                                    <option value="negotiation">Negotiation</option>
                                                    <option value="closed_won">Closed Won</option>
                                                    <option value="closed_lost">Closed Lost</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Probability (%)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={selectedNode.data.opportunity?.probability || ''}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'opportunity', 'probability', parseInt(e.target.value))}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="10"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Type
                                                </label>
                                                <select
                                                    value={selectedNode.data.opportunity?.type || 'new_business'}
                                                    onChange={(e) => updateNodeNested(selectedNode.id, 'opportunity', 'type', e.target.value)}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="new_business">New Business</option>
                                                    <option value="upsell">Upsell</option>
                                                    <option value="cross_sell">Cross Sell</option>
                                                    <option value="renewal">Renewal</option>
                                                    <option value="relocation">Relocation</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Description
                                            </label>
                                            <textarea
                                                value={selectedNode.data.opportunity?.description || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'opportunity', 'description', e.target.value)}
                                                rows={3}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Opportunity description"
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'update_opportunity' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Opportunity *
                                            </label>
                                            <select
                                                value={selectedNode.data.opportunity?.opportunityId || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'opportunity', 'opportunityId', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            >
                                                <option value="">Select an opportunity...</option>
                                                {opportunitiesLoading ? (
                                                    <option disabled>Loading opportunities...</option>
                                                ) : opportunities.length === 0 ? (
                                                    <option disabled>No opportunities available</option>
                                                ) : (
                                                    opportunities.map(opp => (
                                                        <option key={opp._id} value={opp._id}>
                                                            {opp.name} ({opp.stage})
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Stage
                                                </label>
                                                <select
                                                    value={selectedNode.data.opportunity?.updates?.stage || ''}
                                                    onChange={(e) => {
                                                        const updates = { ...selectedNode.data.opportunity?.updates, stage: e.target.value || undefined };
                                                        updateNodeNested(selectedNode.id, 'opportunity', 'updates', updates);
                                                    }}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                >
                                                    <option value="">No change</option>
                                                    <option value="prospecting">Prospecting</option>
                                                    <option value="qualification">Qualification</option>
                                                    <option value="proposal">Proposal</option>
                                                    <option value="negotiation">Negotiation</option>
                                                    <option value="closed_won">Closed Won</option>
                                                    <option value="closed_lost">Closed Lost</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-foreground mb-1">
                                                    Probability (%)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={selectedNode.data.opportunity?.updates?.probability || ''}
                                                    onChange={(e) => {
                                                        const updates = { ...selectedNode.data.opportunity?.updates, probability: e.target.value ? parseInt(e.target.value) : undefined };
                                                        updateNodeNested(selectedNode.id, 'opportunity', 'updates', updates);
                                                    }}
                                                    className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                    style={{ '--tw-ring-color': branding.primaryColor }}
                                                    placeholder="No change"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'delete_opportunity' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Opportunity *
                                            </label>
                                            <select
                                                value={selectedNode.data.opportunity?.opportunityId || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'opportunity', 'opportunityId', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            >
                                                <option value="">Select an opportunity...</option>
                                                {opportunitiesLoading ? (
                                                    <option disabled>Loading opportunities...</option>
                                                ) : opportunities.length === 0 ? (
                                                    <option disabled>No opportunities available</option>
                                                ) : (
                                                    opportunities.map(opp => (
                                                        <option key={opp._id} value={opp._id}>
                                                            {opp.name} ({opp.stage})
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <p className="text-sm text-yellow-800">
                                                ⚠️ This will permanently delete the selected opportunity. This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'update_entity_params' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Entity Type *
                                            </label>
                                            <select
                                                value={selectedNode.data.entityUpdate?.entity || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'entityUpdate', 'entity', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            >
                                                <option value="">Select entity type...</option>
                                                <option value="lead">Lead</option>
                                                <option value="client">Client</option>
                                                <option value="account">Account</option>
                                                <option value="opportunity">Opportunity</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Entity ID *
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedNode.data.entityUpdate?.entityId || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'entityUpdate', 'entityId', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Entity ID (from trigger or enter manually)"
                                                required
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Use variables like {'{'}clientId{'}'} or {'{'}leadId{'}'} from trigger, or enter a specific ID
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Updates (JSON) *
                                            </label>
                                            <textarea
                                                value={typeof selectedNode.data.entityUpdate?.updates === 'object' ? JSON.stringify(selectedNode.data.entityUpdate.updates, null, 2) : selectedNode.data.entityUpdate?.updates || ''}
                                                onChange={(e) => {
                                                    try {
                                                        const updates = JSON.parse(e.target.value);
                                                        updateNodeNested(selectedNode.id, 'entityUpdate', 'updates', updates);
                                                    } catch {
                                                        // Invalid JSON, store as string for now
                                                        updateNodeNested(selectedNode.id, 'entityUpdate', 'updates', e.target.value);
                                                    }
                                                }}
                                                rows={6}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring font-mono text-sm"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder='{"status": "active", "priority": "high"}'
                                                required
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Enter field updates as JSON object
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                {selectedNode.type === 'action' && selectedNode.data.type === 'start_campaign' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Campaign *
                                            </label>
                                            <select
                                                value={selectedNode.data.campaign?.campaignId || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'campaign', 'campaignId', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                required
                                            >
                                                <option value="">Select a journey campaign...</option>
                                                {campaignsLoading ? (
                                                    <option disabled>Loading campaigns...</option>
                                                ) : campaigns.length === 0 ? (
                                                    <option disabled>No journey campaigns available</option>
                                                ) : (
                                                    campaigns.map(campaign => (
                                                        <option key={campaign._id} value={campaign._id}>
                                                            {campaign.name} ({campaign.type})
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Only journey campaigns can be started from workflows
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Contact ID (optional)
                                            </label>
                                            <input
                                                type="text"
                                                value={selectedNode.data.campaign?.contactId || ''}
                                                onChange={(e) => updateNodeNested(selectedNode.id, 'campaign', 'contactId', e.target.value)}
                                                className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:border-ring"
                                                style={{ '--tw-ring-color': branding.primaryColor }}
                                                placeholder="Leave empty to use client from trigger"
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                If empty, will use the client/contact from the workflow trigger
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ReactFlowProvider>
    );
};

export default VisualWorkflowBuilder;
